import styled from "@emotion/styled";
import { createFocusRingStyles } from "../../../util/createFocusRingStyles";
import { RequiredThemeKeys } from "../../../util/theme";
import { withFocusToggleClassName } from "../../../util/withFocusClassName";
import { checkboxBorderTheme, checkboxColors, checkboxMotionTheme } from "../checkboxTheme";

const getSize = (props: CheckboxIconProps) => props.size;
interface CheckboxIconProps {
    appearance: "check" | "flag" | "star";
    size: 16 | 20;
    themeMode: RequiredThemeKeys;
}

export const CheckboxIcon = withFocusToggleClassName(styled("div")<CheckboxIconProps>`
    // Both of these are needed to position the input fully over the icon.
    position: relative;
    isolation: isolate;

    // Focus ring.
    ${({ themeMode }) =>
        createFocusRingStyles({
            theme: themeMode,
            gap: 1,
            // 0 here since the element this is attached to doesn't have a border.
            componentBorderWidth: 0,
            componentRadius: checkboxBorderTheme.borderRadius(themeMode),
            focusSelector: ":focus-within",
        })}

    // The following styles are used to drive the checkbox styling entirely via CSS, rather than
    // using CSS in JS. This is for performance in the Grid and other complex UI. A single
    // re-render, React dev-mode on a very fast computer, is usually around 1.3ms. Keeping these
    // hover changes in CSS contributes to a larger ecosystem of perf.

    //  Base Styles
    > input + [data-checkbox-wrapper],
    > input + [data-checkbox-wrapper] > * {
        transition: ${({ themeMode }) => checkboxMotionTheme.background(themeMode)};
    }

    > input + [data-checkbox-wrapper] > * {
        width: ${getSize}px;
    }

    // Rest unchecked
    > input:not(:checked) + [data-checkbox-wrapper] {
        background-color: ${({ themeMode, appearance }) =>
            checkboxColors[appearance].background.rest.unchecked(themeMode)};
        border-color: ${({ themeMode, appearance }) => checkboxColors[appearance].border.rest.unchecked(themeMode)};
        > * {
            color: ${({ themeMode, appearance }) => checkboxColors[appearance].icon.rest.unchecked(themeMode)};
        }
        // to handle the dual-tone unchecked flag/star outline
        path + path {
            color: ${({ themeMode, appearance }) => checkboxColors[appearance].icon.rest.outline(themeMode)};
        }
    }

    > input:not(:checked):not(:indeterminate) + div span svg path {
        stroke-dasharray: 1000;
        stroke-dashoffset: 1000;
    }

    // Rest checked
    > input:checked + [data-checkbox-wrapper],
    > input:indeterminate + [data-checkbox-wrapper] {
        background-color: ${({ themeMode, appearance }) =>
            checkboxColors[appearance].background.rest.checked(themeMode)};
        border-color: ${({ themeMode, appearance }) => checkboxColors[appearance].border.rest.checked(themeMode)};
    }

    > input:checked + [data-checkbox-wrapper] > * {
        color: ${({ themeMode, appearance }) => checkboxColors[appearance].icon.rest.checked(themeMode)};
    }

    > input:checked + div span svg path {
        // these are only for the checkmark, but don't seem to negatively affect the others
        stroke-dasharray: 1000;
        stroke-dashoffset: 0;
        transition: ${({ themeMode, appearance }) => checkboxMotionTheme[appearance](themeMode)};
    }

    // Hover unchecked
    > input:not(:checked):hover + [data-checkbox-wrapper] {
        background-color: ${({ themeMode, appearance }) =>
            checkboxColors[appearance].background.hover.unchecked(themeMode)};
        border-color: ${({ themeMode, appearance }) => checkboxColors[appearance].border.hover.unchecked(themeMode)};
        > * {
            color: ${({ themeMode, appearance }) => checkboxColors[appearance].icon.hover.unchecked(themeMode)};
        }
    }

    // Hover checked
    > input:checked:hover + [data-checkbox-wrapper],
    > input:indeterminate:hover + [data-checkbox-wrapper] {
        background-color: ${({ themeMode, appearance }) =>
            checkboxColors[appearance].background.hover.checked(themeMode)};
        border-color: ${({ themeMode, appearance }) => checkboxColors[appearance].border.hover.checked(themeMode)};
    }

    // The check and dash icons have a different color when checked and hovered.
    > input:not(:checked):hover + [data-checkbox-wrapper] > * {
        color: ${({ themeMode, appearance }) => checkboxColors[appearance].icon.hover.unchecked(themeMode)};
    }
    > input:checked:hover + [data-checkbox-wrapper] > *,
    > input:indeterminate:hover + [data-checkbox-wrapper] > * {
        color: ${({ themeMode, appearance }) => checkboxColors[appearance].icon.hover.checked(themeMode)};
    }

    // Active unchecked
    > input:not(:checked):active + [data-checkbox-wrapper] {
        background-color: ${({ themeMode, appearance }) =>
            checkboxColors[appearance].background.active.unchecked(themeMode)};
        border-color: ${({ themeMode, appearance }) => checkboxColors[appearance].border.active.unchecked(themeMode)};
        > * {
            color: ${({ themeMode, appearance }) => checkboxColors[appearance].icon.active.unchecked(themeMode)};
        }
    }

    // Active checked
    > input:checked:active + [data-checkbox-wrapper],
    > input:indeterminate:active + [data-checkbox-wrapper] {
        background-color: ${({ themeMode, appearance }) =>
            checkboxColors[appearance].background.active.checked(themeMode)};
        border-color: ${({ themeMode, appearance }) => checkboxColors[appearance].border.active.checked(themeMode)};
        > * {
            color: ${({ themeMode, appearance }) => checkboxColors[appearance].icon.active.checked(themeMode)};
        }
    }

    // Disabled unchecked - we currently support both the disabled attribute and the aria-disabled attribute,
    // unfortunately, though we will remove the disabled attr support in a future major release.
    > input:not(:checked):disabled + [data-checkbox-wrapper],
    > input:not(:checked)[aria-disabled="true"] + [data-checkbox-wrapper] {
        background-color: ${({ themeMode, appearance }) =>
            checkboxColors[appearance].background.disabled.unchecked(themeMode)};
        border-color: ${({ themeMode, appearance }) => checkboxColors[appearance].border.disabled.unchecked(themeMode)};
        > * {
            color: ${({ themeMode, appearance }) => checkboxColors[appearance].icon.disabled.unchecked(themeMode)};
        }
        path + path {
            color: ${({ themeMode, appearance }) => checkboxColors[appearance].icon.disabled.outline(themeMode)};
        }
    }

    // Disabled checked
    > input:checked:disabled + [data-checkbox-wrapper],
    > input:checked[aria-disabled="true"] + [data-checkbox-wrapper] {
        background-color: ${({ themeMode, appearance }) =>
            checkboxColors[appearance].background.disabled.checked(themeMode)};
        border-color: ${({ themeMode, appearance }) => checkboxColors[appearance].border.disabled.checked(themeMode)};
        > * {
            color: ${({ themeMode, appearance }) => checkboxColors[appearance].icon.disabled.checked(themeMode)};
        }
    }

    // Invalid unchecked rest
    > input:not(:checked)[data-is-invalid="true"] + [data-checkbox-wrapper] {
        // Invalid unchecked rest uses the same background styles as valid unchecked rest.
        background-color: ${({ themeMode, appearance }) =>
            checkboxColors[appearance].background.rest.unchecked(themeMode)};
        border-color: ${({ themeMode, appearance }) =>
            checkboxColors[appearance].border.invalid.rest.unchecked(themeMode)};
        path + path {
            color: ${({ themeMode, appearance }) => checkboxColors[appearance].icon.invalid.rest(themeMode)};
        }
    }

    // Invalid checked rest
    > input:checked[data-is-invalid="true"] + [data-checkbox-wrapper] {
        background-color: ${({ themeMode, appearance }) =>
            checkboxColors[appearance].background.invalid.rest.checked(themeMode)};
        border-color: ${({ themeMode, appearance }) =>
            checkboxColors[appearance].border.invalid.rest.checked(themeMode)};
        > * {
            color: ${({ themeMode, appearance }) => checkboxColors[appearance].icon.invalid.rest(themeMode)};
        }
    }

    // Invalid unchecked hover
    > input:not(:checked)[data-is-invalid="true"]:hover + [data-checkbox-wrapper] {
        // Invalid unchecked hover uses the same background styles as valid unchecked hover.
        background-color: ${({ themeMode, appearance }) =>
            checkboxColors[appearance].background.hover.unchecked(themeMode)};
        border-color: ${({ themeMode, appearance }) =>
            checkboxColors[appearance].border.invalid.hover.unchecked(themeMode)};
        path + path {
            color: ${({ themeMode, appearance }) => checkboxColors[appearance].icon.invalid.hover(themeMode)};
        }
    }

    // Invalid checked hover
    > input:checked[data-is-invalid="true"]:hover + [data-checkbox-wrapper] {
        background-color: ${({ themeMode, appearance }) =>
            checkboxColors[appearance].background.invalid.hover.checked(themeMode)};
        border-color: ${({ themeMode, appearance }) =>
            checkboxColors[appearance].border.invalid.hover.checked(themeMode)};
        > * {
            color: ${({ themeMode, appearance }) => checkboxColors[appearance].icon.invalid.hover(themeMode)};
        }
    }

    // Invalid unchecked active
    > input:not(:checked)[data-is-invalid="true"]:active + [data-checkbox-wrapper] {
        // Invalid unchecked active uses the same background styles as valid unchecked active.
        background-color: ${({ themeMode, appearance }) =>
            checkboxColors[appearance].background.active.unchecked(themeMode)};
        border-color: ${({ themeMode, appearance }) =>
            checkboxColors[appearance].border.invalid.active.unchecked(themeMode)};
        path + path {
            color: ${({ themeMode, appearance }) => checkboxColors[appearance].icon.invalid.active(themeMode)};
        }
    }

    // Invalid checked active
    > input:checked[data-is-invalid="true"]:active + [data-checkbox-wrapper] {
        background-color: ${({ themeMode, appearance }) =>
            checkboxColors[appearance].background.invalid.active.checked(themeMode)};
        border-color: ${({ themeMode, appearance }) =>
            checkboxColors[appearance].border.invalid.active.checked(themeMode)};
        > * {
            color: ${({ themeMode, appearance }) => checkboxColors[appearance].icon.invalid.active(themeMode)};
        }
    }
`);
